import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Wine as WineIcon } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { IMG } from "@/data/brand";
import {
  menuSections, menuMeta, dinner, barBites, desserts, cigars, specials, wine,
} from "@/data/menuData";

// ---- elegant menu row (typography-led, dotted leader) ----
const Row = ({ item }) => (
  <div className="flex items-baseline gap-3 py-3 group" data-testid={`menu-item-${item.name}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-serif text-xl md:text-2xl text-white/95 group-hover:text-[#c5a059] transition-colors leading-tight">
          {item.name}
        </h4>
        {item.featured && <span className="text-[9px] tracking-[0.2em] uppercase text-[#0a0908] bg-[#c5a059] px-2 py-0.5">Signature</span>}
        {item.gf && <span className="text-[9px] tracking-[0.15em] uppercase text-white/40 border border-white/20 px-1.5 py-0.5">GF</span>}
      </div>
      {item.desc && <p className="text-white/45 text-sm font-light mt-1 leading-relaxed">{item.desc}</p>}
    </div>
    <div className="flex-1 border-b border-dotted border-white/15 mb-1.5 min-w-[12px] max-w-[80px]" />
    <span className="font-serif text-lg md:text-xl text-[#c5a059] whitespace-nowrap">
      {item.price ? (item.mp && item.price.includes("MP") ? item.price : `$${item.price}`) : "MP"}
    </span>
  </div>
);

const SubBlock = ({ title, items }) => (
  <div className="mb-14">
    <h3 className="font-serif italic text-2xl md:text-3xl text-[#c5a059] mb-5">{title}</h3>
    <div className="divide-y divide-white/5">
      {items.map((i) => <Row key={i.name} item={i} />)}
    </div>
  </div>
);

// ---- DINNER (sub-category selector + signature callouts + disclaimers) ----
const DinnerSection = () => {
  const cats = Object.keys(dinner.subcats);
  const [active, setActive] = useState(cats[0]);
  return (
    <div>
      <div className="text-center mb-10">
        <p className="text-white/50 text-sm font-light">General Manager {menuMeta.gm} &nbsp;·&nbsp; Executive Chef {menuMeta.execChef}</p>
      </div>
      {/* sub-category scroller */}
      <div className="-mx-6 md:mx-0 px-6 md:px-0 overflow-x-auto no-scrollbar mb-12">
        <div className="flex md:flex-wrap md:justify-center gap-x-7 gap-y-3 w-max md:w-auto border-b border-white/10 pb-5">
          {cats.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`dinner-cat-${c}`}
              className={`relative text-[11px] font-medium tracking-[0.2em] uppercase whitespace-nowrap transition-colors ${active === c ? "text-white" : "text-white/40 hover:text-white/70"}`}>
              {c}
              {active === c && <motion.span layoutId="dinner-ul" className="absolute -bottom-[21px] left-0 right-0 h-px bg-[#c5a059]" />}
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={active} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
          className="max-w-3xl mx-auto">
          <SubBlock title={active} items={dinner.subcats[active]} />
        </motion.div>
      </AnimatePresence>

      {/* steak statement + temp guide */}
      <div className="max-w-3xl mx-auto mt-6 border border-white/10 p-8 bg-[#0f0d0c]">
        <p className="font-serif italic text-lg text-white/90 text-center">{menuMeta.steakStatement}</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-8">
          {menuMeta.tempGuide.map(([t, d]) => (
            <div key={t} className="text-center">
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#c5a059]">{t}</p>
              <p className="text-white/50 text-xs font-light mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-3xl mx-auto mt-6 space-y-2">
        <p className="text-white/35 text-xs font-light leading-relaxed">{menuMeta.disclaimer}</p>
        <p className="text-white/35 text-xs font-light leading-relaxed">{menuMeta.cardFee}</p>
      </div>
    </div>
  );
};

const SimpleSection = ({ data }) => (
  <div className="max-w-3xl mx-auto">
    {data.note && <p className="text-center font-serif italic text-lg text-white/70 mb-12">{data.note}</p>}
    {Object.entries(data.subcats).map(([title, items]) => <SubBlock key={title} title={title} items={items} />)}
  </div>
);

// ---- CIGARS (Learn More per cigar) ----
const CigarSection = () => {
  const [open, setOpen] = useState(null);
  return (
    <div className="max-w-3xl mx-auto">
      <p className="text-center font-serif italic text-lg text-white/70 mb-12">{cigars.note}</p>
      {cigars.groups.map((g) => (
        <div key={g.body} className="mb-12">
          <h3 className="font-serif italic text-2xl md:text-3xl text-[#c5a059] mb-5">{g.body}</h3>
          <div className="divide-y divide-white/5">
            {g.items.map((c) => {
              const id = c.name;
              const isOpen = open === id;
              return (
                <div key={id} className="py-3" data-testid={`cigar-${c.name}`}>
                  <div className="flex items-baseline gap-3">
                    <button onClick={() => setOpen(isOpen ? null : id)} className="flex items-center gap-2 text-left flex-1 group">
                      {isOpen ? <Minus size={14} className="text-[#c5a059]" /> : <Plus size={14} className="text-[#c5a059]" />}
                      <span className="font-serif text-xl md:text-2xl text-white/95 group-hover:text-[#c5a059] transition-colors">{c.name}</span>
                    </button>
                    <div className="flex-1 border-b border-dotted border-white/15 mb-1.5 min-w-[12px] max-w-[80px]" />
                    <span className="font-serif text-lg md:text-xl text-[#c5a059]">${c.price}</span>
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden text-white/45 text-sm font-light pl-6 mt-2">
                        {g.body} body. Full tasting notes and pairing suggestions available from your server on the cigar patio.
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---- SPECIALS ----
const SpecialsSection = () => (
  <div className="max-w-4xl mx-auto">
    <div className="border border-[#c5a059]/30 p-8 md:p-10 mb-12 bg-[#0f0d0c]">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059]">Happy Hour</p>
      <p className="font-serif text-2xl text-white mt-2">{specials.happyHour.when}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
        {specials.happyHour.items.map((i) => (
          <div key={i} className="flex items-center gap-2 text-white/70 text-sm font-light"><span className="w-1 h-1 bg-[#c5a059] rounded-full" />{i}</div>
        ))}
      </div>
      <p className="text-white/35 text-xs font-light mt-5">{specials.happyHour.note}</p>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-12 gap-y-8">
      {specials.weekly.map((w) => (
        <div key={w.title} className="border-l border-[#c5a059]/40 pl-5">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#c5a059]">{w.day}</p>
          <h3 className="font-serif text-2xl text-white mt-1">{w.title}</h3>
          <p className="text-white/50 text-sm font-light mt-2 leading-relaxed">{w.body}</p>
        </div>
      ))}
    </div>
  </div>
);

// ---- WINE & COCKTAILS ----
const WineSection = () => (
  <div className="max-w-3xl mx-auto">
    <div className="border border-[#c5a059]/30 p-8 md:p-10 mb-14 text-center bg-[#0f0d0c]">
      <WineIcon className="mx-auto text-[#c5a059] mb-4" size={26} />
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059] mb-3">Award-Winning Wine Program</p>
      <p className="text-white/70 font-light leading-relaxed max-w-2xl mx-auto">{wine.award}</p>
      <div className="mt-7">
        <LuxButton href={menuMeta.wineListPdf} variant="outline-light" testid="wine-full-list">View Full Wine List</LuxButton>
      </div>
    </div>

    <SubBlock title="Cocktails" items={wine.cocktails} />

    <h3 className="font-serif italic text-2xl md:text-3xl text-[#c5a059] mb-5">The Cellar</h3>
    <div className="flex flex-wrap gap-3 mb-6">
      {wine.categories.map((c) => (
        <span key={c} className="text-xs tracking-[0.15em] uppercase text-white/60 border border-white/15 px-4 py-2.5">{c}</span>
      ))}
    </div>
    <p className="text-white/35 text-xs font-light">Full by-the-glass and bottle selections rotate seasonally — see the complete wine list or ask your sommelier.</p>
  </div>
);

const MenuPage = () => {
  const location = useLocation();
  const [section, setSection] = useState("dinner");

  useEffect(() => {
    const s = new URLSearchParams(location.search).get("section");
    if (s && menuSections.some((m) => m.id === s)) {
      setSection(s);
    }
  }, [location.search]);

  const selectSection = (id) => {
    setSection(id);
    if (window.lenis) window.lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  };

  return (
    <main data-testid="menu-page">
      <PageHero kicker="Steaks · Seafood · Raw Bar" title={["The Menu"]} image={IMG.steakPlated} height="h-[55vh]" />

      {/* Section selector — horizontally scrollable on mobile */}
      <div className="sticky top-[64px] z-20 bg-[#0a0908]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1400px] mx-auto overflow-x-auto no-scrollbar">
          <div className="flex justify-start md:justify-center gap-x-8 px-6 py-5 w-max md:w-auto mx-auto">
            {menuSections.map((s) => (
              <button key={s.id} onClick={() => selectSection(s.id)} data-testid={`menu-section-${s.id}`}
                className={`relative text-[11px] md:text-xs font-medium tracking-[0.2em] uppercase whitespace-nowrap transition-colors ${section === s.id ? "text-[#c5a059]" : "text-white/50 hover:text-white"}`}>
                {s.label}
                {section === s.id && <motion.span layoutId="section-ul" className="absolute -bottom-[19px] left-0 right-0 h-0.5 bg-[#c5a059]" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="bg-[#0a0908] py-16 md:py-24 grain relative min-h-[60vh]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <AnimatePresence mode="wait">
            <motion.div key={section} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              {section === "dinner" && <DinnerSection />}
              {section === "bar-bites" && <SimpleSection data={barBites} />}
              {section === "desserts" && <SimpleSection data={desserts} />}
              {section === "wine" && <WineSection />}
              {section === "cigars" && <CigarSection />}
              {section === "specials" && <SpecialsSection />}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Reserve CTA */}
      <section className="relative py-24 text-center overflow-hidden">
        <img src={IMG.reservationTable} alt="Reserve" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-light">Ready to dine with us?</h2>
            <div className="mt-8"><LuxButton to="/reservations" variant="gold" testid="menu-reserve-btn">Reserve a Table</LuxButton></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default MenuPage;
